import React, { useContext, useEffect, useState } from "react";
import TableCustom from "../../../../../../../components/Table";
import { LocalizationContext } from "../../../../../../../locales/Translation";
import SizeBox from "../../../../../../../components/SizeBox";
import {
  dashboardFilterDateRangeSelector,
  dashboardDayOfficeBookingFilterSelector,
  dashboardDayOfficeBookingSelector,
} from "../../../../../selectors/DashboardSelectors";
import { useDispatch, useSelector } from "react-redux";
import { DayOfficeBookingType } from "../../../../../configs/DeclareTypes";
import { dashboardDayOfficeBookingFilterAction } from "../../../../../actions/DashboardActions";
import { dashboardDayOfficeBookingThunk } from "../../../../../thunks/DashboardThunks";
import { userCompanyChooseSelector } from "../../../../../../User/selectors/UserSelectors";
import {
  ORDER_BY_ASC,
  ORDER_BY_DESC,
  ORDER_BY_NONE,
} from "../../../../../../App/configs/Constants";
import { PRODUCT_TYPE_BUSINESS } from "../../../../../configs/Constants";

const TableDayOfficeBookings = () => {
  const { translations } = useContext(LocalizationContext);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const dashboardDayOfficeBooking = useSelector(
    dashboardDayOfficeBookingSelector
  );
  const dashboardDayOfficeBookingFilter = useSelector(
    dashboardDayOfficeBookingFilterSelector
  );
  const userCompanyChoose = useSelector(userCompanyChooseSelector);
  const dashboardFilterDateRange = useSelector(
    dashboardFilterDateRangeSelector
  );

  useEffect(() => {
    (async () => {
      try {
        if (dashboardFilterDateRange === null) {
          return;
        }
        setLoading(true);
        await dispatch<any>(
          dashboardDayOfficeBookingThunk(PRODUCT_TYPE_BUSINESS)
        );
      } catch (e) {
        console.log("e", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [
    dashboardDayOfficeBookingFilter,
    userCompanyChoose,
    dashboardFilterDateRange,
  ]);

  const dayOfficeField = ({
    day_office_name,
    property_name,
  }: Partial<DayOfficeBookingType>) => (
    <>
      <div className={"table_title"}>
        {translations.dayOffices.dayOfficesName}:
      </div>
      <div>{day_office_name}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.property}:</div>
      <div>{property_name}</div>
    </>
  );

  const dateTimeField = ({
    start_time,
    duration,
    created_time,
  }: Partial<DayOfficeBookingType>) => (
    <>
      <div className={"table_title"}>{translations.createdTime}:</div>
      <div>{created_time}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.startTime}:</div>
      <div>{start_time}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.duration}:</div>
      <div>{duration}</div>
    </>
  );

  const costField = ({
    hourly_rate,
    total_price,
  }: {
    hourly_rate: string;
    total_price: string;
  }) => (
    <>
      <div className={"table_title"}>{translations.hourlyRate}:</div>
      <div>{hourly_rate}</div>
      <SizeBox height={"10px"} />
      <div className={"table_title"}>{translations.totalPrice}:</div>
      <div>{total_price}</div>
    </>
  );

  const statusField = ({
    status_id,
    refund_amount,
    refund_status_id,
  }: {
    status_id: number;
    refund_amount: string | null;
    refund_status_id: number | null;
  }) => (
    <>
      <div>{translations.roomBookingStatus[status_id]}</div>
      {refund_status_id ? (
        <div>
          ({translations.roomBookingRefundStatus[refund_status_id]}
          {refund_amount ? ` - ${refund_amount}` : ""})
        </div>
      ) : null}
    </>
  );

  const _formatInputData = () => {
    return dashboardDayOfficeBooking?.items?.map(
      ({
        transaction_id,
        employee_name,
        day_office_name,
        property_name,
        start_time,
        duration,
        hourly_rate,
        total_price,
        refund_amount,
        refund_status_id,
        status_id,
        created_time,
      }: DayOfficeBookingType) => {
        return {
          transaction_id,
          employee_name,
          dayOfficeField: dayOfficeField({
            property_name,
            day_office_name,
          }),
          dateTimeField: dateTimeField({ created_time, start_time, duration }),
          costField: costField({ hourly_rate, total_price }),
          statusField: statusField({
            status_id,
            refund_amount,
            refund_status_id,
          }),
        };
      }
    );
  };

  const columns = [
    { key: "day_office_by_transaction_id", col: translations.transactionID },
    { key: "day_office_by_employee_name", col: translations.userName },
    {
      key: "day_office_by_day_office",
      col: translations.dayOffices.office,
    },
    { key: "day_office_by_start_time", col: translations.dateTime },
    { key: "day_office_by_cost", col: translations.cost },
    { key: "day_office_by_status", col: translations.status },
  ];

  const _onChangeSortCol = (col: any) => {
    if (dashboardDayOfficeBookingFilter.sort_by !== col.key) {
      dispatch(
        dashboardDayOfficeBookingFilterAction({
          key: "sort_by",
          value: col.key,
        })
      );
      dispatch(
        dashboardDayOfficeBookingFilterAction({
          key: "order_by",
          value: ORDER_BY_ASC,
        })
      );
    } else {
      dispatch(
        dashboardDayOfficeBookingFilterAction({
          key: "sort_by",
          value: col.key,
        })
      );
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_NONE) {
        dispatch(
          dashboardDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_ASC,
          })
        );
      }
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_ASC) {
        dispatch(
          dashboardDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_DESC,
          })
        );
      }
      if (dashboardDayOfficeBookingFilter.order_by === ORDER_BY_DESC) {
        dispatch(
          dashboardDayOfficeBookingFilterAction({
            key: "order_by",
            value: ORDER_BY_NONE,
          })
        );
      }
    }
  };

  return (
    <TableCustom
      orderBy={dashboardDayOfficeBookingFilter.order_by}
      sortBy={dashboardDayOfficeBookingFilter.sort_by}
      sortTable={true}
      onChangeSortCol={_onChangeSortCol}
      tableData={
        dashboardDayOfficeBooking?.items?.length > 0 ? _formatInputData() : []
      }
      columns={columns}
      loading={loading}
      totalItems={dashboardDayOfficeBooking?.pagination?.total}
      currentPage={dashboardDayOfficeBooking?.pagination?.page}
      showTotal
      unit={translations.transactions}
      itemPerPage={dashboardDayOfficeBooking?.pagination?.limit}
      onChangePagination={(_: any, { activePage }: any) => {
        dispatch(
          dashboardDayOfficeBookingFilterAction({
            key: "page",
            value: activePage,
          })
        );
      }}
    />
  );
};

export default TableDayOfficeBookings;
